/**
 * BudgetChangeRequestService
 * Manager-raised requests to change an APPROVED budget; BudgetAdmins decide.
 * Routes: api/budgets/{budgetId}/change-requests (create),
 *         api/budget-change-requests (+ /mine, /{id}/approve, /{id}/reject)
 */
export class BudgetChangeRequestService {
    constructor(http, config) {
        this.http = http;
        this.servicePath = config?.servicePath ?? BudgetChangeRequestService.DEFAULT_SERVICE_PATH;
    }
    buildUrl(path) {
        const url = `${this.servicePath}/${path}`;
        return url.startsWith('/') ? url : '/' + url;
    }
    buildFilteredUrl(path, filter) {
        const params = new URLSearchParams();
        if (filter?.statusId !== undefined)
            params.append('statusId', String(filter.statusId));
        if (filter?.budgetId !== undefined)
            params.append('budgetId', String(filter.budgetId));
        const qs = params.toString();
        const url = this.buildUrl(path);
        return qs ? `${url}?${qs}` : url;
    }
    /** Raise a change request against an approved budget (managers). */
    async create(budgetId, body) {
        const response = await this.createRaw(budgetId, body);
        if (!response.data)
            throw new Error(`Failed to create change request for budget ${budgetId}`);
        return response.data;
    }
    createRaw(budgetId, body) {
        return this.http.post(this.buildUrl(`api/budgets/${budgetId}/change-requests`), body);
    }
    /** Admin review queue, optionally filtered by status and/or budget. */
    async getAll(filter) {
        const response = await this.getAllRaw(filter);
        return response.data ?? [];
    }
    getAllRaw(filter) {
        return this.http.get(this.buildFilteredUrl('api/budget-change-requests', filter));
    }
    /** The calling user's own change requests (any authenticated user). */
    async getMine(filter) {
        const response = await this.getMineRaw(filter);
        return response.data ?? [];
    }
    getMineRaw(filter) {
        return this.http.get(this.buildFilteredUrl('api/budget-change-requests/mine', filter));
    }
    /** Approve a Pending request — APPLIES the amendment + re-arms thresholds (BudgetAdmin). */
    async approve(id, body) {
        const response = await this.approveRaw(id, body);
        if (!response.data)
            throw new Error(`Failed to approve change request ${id}`);
        return response.data;
    }
    approveRaw(id, body) {
        return this.http.put(this.buildUrl(`api/budget-change-requests/${id}/approve`), body ?? {});
    }
    /** Reject a Pending request — comments are REQUIRED (BudgetAdmin). */
    async reject(id, body) {
        const response = await this.rejectRaw(id, body);
        if (!response.data)
            throw new Error(`Failed to reject change request ${id}`);
        return response.data;
    }
    rejectRaw(id, body) {
        return this.http.put(this.buildUrl(`api/budget-change-requests/${id}/reject`), body);
    }
}
BudgetChangeRequestService.DEFAULT_SERVICE_PATH = 'budgets';
//# sourceMappingURL=BudgetChangeRequestService.js.map